import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { collection, getDocs, query, where, doc, getDoc, deleteDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import LoadingSpinner from "../components/LoadingSpinner";
import EmptyState from "../components/EmptyState";

const SavedInternships = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [savedInternships, setSavedInternships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    const fetchSavedInternships = async () => {
      try {
        const q = query(collection(db, "bookmarks"), where("userId", "==", user.uid));
        const querySnapshot = await getDocs(q);
        const internshipIds = querySnapshot.docs.map((d) => ({
          id: d.id,
          internshipId: d.data().internshipId,
        }));

        const internshipPromises = internshipIds.map(({ internshipId }) =>
          getDoc(doc(db, "internships", internshipId))
        );
        const internshipDocs = await Promise.all(internshipPromises);

        const internshipData = internshipDocs
          .filter((d) => d.exists())
          .map((d, index) => ({
            id: internshipIds[index].id,
            internshipId: d.id,
            ...d.data(),
          }));

        setSavedInternships(internshipData);
      } catch (error) {
        console.error("Error fetching saved internships:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSavedInternships();
  }, [user, navigate]);

  const handleRemove = async (bookmarkId) => {
    try {
      await deleteDoc(doc(db, "bookmarks", bookmarkId));
      setSavedInternships(savedInternships.filter((i) => i.id !== bookmarkId));
    } catch (error) {
      console.error("Error removing saved internship:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <LoadingSpinner text="Loading saved items..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="page-container">
        <div className="mb-8">
          <h1 className="section-title">Saved Opportunities</h1>
          <p className="text-gray-500 mt-1">
            {savedInternships.length} saved {savedInternships.length === 1 ? "item" : "items"}
          </p>
        </div>

        {savedInternships.length === 0 ? (
          <EmptyState
            title="Nothing saved yet"
            message="Bookmark internships and placements to find them here later."
            icon={
              <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            }
          />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 animate-fade-in">
            {savedInternships.map((job) => (
              <div key={job.id} className="card">
                <div className="mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                  <p className="text-sm text-gray-500">{job.company}</p>
                </div>

                <div className="space-y-2 mb-4">
                  {job.location && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>{job.location}</span>
                    </div>
                  )}
                  {job.duration && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{job.duration} months</span>
                    </div>
                  )}
                  {job.stipend && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>₹{job.stipend.toLocaleString("en-IN")}/month</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                  <a
                    href={job.applyLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="button btn-primary text-sm"
                  >
                    Apply Now
                  </a>
                  <button
                    onClick={() => handleRemove(job.id)}
                    className="button btn-secondary text-sm text-red-600 hover:text-red-700 hover:border-red-200 hover:bg-red-50"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedInternships;
